import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BankAddEditModal from "./Components/BankAddEditModal";
import BankServices from "../../Services/QRCodeServices/BankServices/BankServices";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import AddDepositModal from "./Components/AddDepositModal";

function BankList() {
  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState(false);
  const [depositModal, setDepositModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleModal = () => setModal(!modal);
  const toggleDepositModal = () => setDepositModal(!depositModal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Bank Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Bank Code",
      selector: (row) => row.bankCode,
      sortable: true,
    },
    {
      name: "Account Number",
      selector: (row) => row.accountNumber,
      sortable: true,
    },

    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="hstack gap-2">
          <Button
            color="btn btn-soft-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <i className="ri-edit-line"></i>
          </Button>
          <Button
            color="btn btn-soft-danger"
            size="sm"
            onClick={() => handleDeleteClick(row)}
          >
            <i className="ri-delete-bin-line"></i>
          </Button>
        </div>
      ),
    },
  ];

  async function fetchBanks() {
    try {
      setLoading(true);
      const fetchedBanks = await BankServices.getList();
      setBanks(fetchedBanks);
      setFilteredBanks(fetchedBanks);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching banks:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    let filtered = banks;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (bank) =>
          bank.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
          bank.accountNumber?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredBanks(filtered);
  }, [searchTerm, banks]);

  const handleEdit = (row) => {
    setIsEdit(true);
    setSelectedBank(row);
    toggleModal();
  };

  const handleCreate = () => {
    setIsEdit(false);
    setSelectedBank(null);
    toggleModal();
  };

  const handleCreateDeposit = () => {
    toggleDepositModal();
  };

  const handleModalSuccess = () => {
    fetchBanks();
    toggleModal();
  };

  const handleDepositModalSuccess = () => {
    fetchBanks();
    toggleDepositModal();
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await BankServices.delete(selectedDeleteItem.id);
        toast.success("Bank deleted successfully");
        fetchBanks();
      } catch (error) {
        toast.error("Failed to delete bank");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Bank List" pageTitle="Banks" />
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <FormGroup className="col-lg-3">
                      <Input
                        type="text"
                        placeholder="Search by Name or Account Number"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                    <div className="ms-auto">
                      <Button
                        color="primary"
                        onClick={handleCreate}
                        className="me-2"
                      >
                        + Create Bank
                      </Button>
                      <Button color="primary" onClick={handleCreateDeposit}>
                        + Create Deposit
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={filteredBanks}
                    customStyles={customStyles}
                    responsive
                    striped
                    highlightOnHover
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <BankAddEditModal
        isOpen={modal}
        toggle={toggleModal}
        isEdit={isEdit}
        selectedBank={selectedBank}
        onSuccess={handleModalSuccess}
      />
      <AddDepositModal
        isOpen={depositModal}
        toggle={toggleDepositModal}
        onSuccess={handleDepositModalSuccess}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
        selectedItem={selectedDeleteItem}
      />
    </React.Fragment>
  );
}

export default BankList;
